import { useCallback } from 'react';
import { graphql, useMutation } from 'react-relay';
import type { RecordSourceSelectorProxy } from 'relay-runtime';
import type {
	useOnDrop_issueTableRow_Mutation,
	useOnDrop_issueTableRow_Mutation$data,
} from '@atlassian/jira-relay/src/__generated__/useOnDrop_issueTableRow_Mutation.graphql';

export interface DropOperation {
	sourceIssueId: string;
	targetIssueId: string;
	edge: 'top' | 'bottom';
}

interface DragAndDropUpdaterParams {
	store: RecordSourceSelectorProxy<useOnDrop_issueTableRow_Mutation$data>;
	dropOperation: DropOperation;
	issuesConnectionId: string;
}

export const runDragAndDropUpdater = ({
	store,
	issuesConnectionId,
	dropOperation,
}: DragAndDropUpdaterParams) => {
	const connection = store.get(issuesConnectionId);
	const edges = connection?.getLinkedRecords('edges');
	if (!connection || !edges) return;
	const sourceEdge = edges.find(
		(edge) => edge?.getLinkedRecord('node')?.getValue('id') === dropOperation.sourceIssueId,
	);
	if (!sourceEdge) return;

	const edgesWithoutSource = edges.filter(
		(edge) => edge?.getLinkedRecord('node')?.getValue('id') !== dropOperation.sourceIssueId,
	);
	const targetEdgeIndex = edgesWithoutSource.findIndex(
		(edge) => edge?.getLinkedRecord('node')?.getValue('id') === dropOperation.targetIssueId,
	);
	edgesWithoutSource.splice(
		targetEdgeIndex + (dropOperation.edge === 'top' ? 0 : 1),
		0,
		sourceEdge,
	);
	connection?.setLinkedRecords(edgesWithoutSource, 'edges');
};

export const useOnDrop = (issuesConnectionId: string) => {
	const [runMutation] = useMutation<useOnDrop_issueTableRow_Mutation>(graphql`
		mutation useOnDrop_issueTableRow_Mutation($rankInput: JiraRankMutationInput!) {
			jira {
				rankIssues(rankInput: $rankInput) @optIn(to: "JiraRankMutation") {
					success
					errors {
						message
						extensions {
							statusCode
							errorType
						}
					}
				}
			}
		}
	`);

	const onDrop = useCallback(
		(dropOperation: DropOperation) => {
			runMutation({
				variables: {
					rankInput: {
						issues: [dropOperation.sourceIssueId],
						relativeToIssue: dropOperation.targetIssueId,
						edge: dropOperation.edge === 'top' ? 'TOP' : 'BOTTOM',
					},
				},
				optimisticUpdater: (store) => {
					runDragAndDropUpdater({
						store,
						dropOperation,
						issuesConnectionId,
					});
				},
				updater: (store, response) => {
					if (response?.jira?.rankIssues?.success) {
						runDragAndDropUpdater({
							store,
							dropOperation,
							issuesConnectionId,
						});
					}
				},
			});
		},
		[issuesConnectionId, runMutation],
	);

	return onDrop;
};
